import { createContext, useContext } from "react";

import type { CommentThreadSubject } from "@hightouch/lib/llm/asset-comments/types";
import {
  type FileType,
  fileTypeHasAgent,
} from "@hightouch/lib/llm/files/types";
import { toSubjectFilter } from "src/components/comments/subject";
import type { AssetCommentThreadsBoolExp } from "src/graphql";

/**
 * The Library file whose comments a surface shows, for a surface on a canvas
 * or in the Library. Its threads are read and written by file, and its agent
 * answers them. Outside one, comments are on their subject alone, as Ad Studio
 * and Lifecycle Studio show them.
 */
export type CommentFile = {
  id: string;
  /** Whether the file's agent answers, so `@Agent` and review agents can be mentioned. */
  hasAgent: boolean;
  /** The brand the file's review agents belong to. */
  brandProfileId: string | null;
};

const CommentFileContext = createContext<CommentFile | null>(null);

export const CommentFileProvider = CommentFileContext.Provider;

export function useCommentFile(): CommentFile | null {
  return useContext(CommentFileContext);
}

export function toCommentFile(
  file: { id: string; type: FileType },
  brandProfileId: string | null,
): CommentFile {
  return {
    id: file.id,
    hasAgent: fileTypeHasAgent(file.type),
    brandProfileId,
  };
}

/** The subject's threads a surface shows: within a file, only the ones its agent answers. */
export function toCommentThreadsFilter(
  subject: CommentThreadSubject,
  file: CommentFile | null,
): AssetCommentThreadsBoolExp {
  return file
    ? { _and: [toSubjectFilter(subject), { amp_file_id: { _eq: file.id } }] }
    : toSubjectFilter(subject);
}
